// src/utils/sessionStorageManager.ts
import type { MenuItem } from "~/types/new_types";
import { getItemName, getItemDescription, getOptionStorageKey } from "~/utils/menuItemHelper";
import type { ItemCard } from "~/hooks/useItemCards";

export interface SessionStorageItemData {
  itemId: string;
  itemName: string;
  itemDescription?: string;
  itemImage?: string;
  basePrice: number;
  totalQuantity: number;
  itemNotes?: string[];  // Array of notes for this item
  cards?: Array<{
    cardId: string;
    cardName: string;
    quantity: number;
    unitBasePrice?: number;
    unitAddOnTotal?: number;
    isAiSuggestion?: boolean;
    variants: Array<{
      name: string;
      key?: string;
      variantType?: string;
      actionType?: string;
      isVariant?: boolean;
      isMultiple?: boolean;
      children: Array<{
        name: string;
        price: number;
        imageUrl?: string;
        comboData?: {
          items?: Array<{
            item_name?: string;
            itemId?: string;
            item_nameNormalized?: string;
            quantity?: number;
            note?: string;
          }>;
          combo_price?: number;
        };
        includedItems?: Array<{
          name: string;
          quantity: number;
          note?: string;
        }>;
      }>;
      quantity: number;
    }>;
  }>;
  // Legacy support
  variants?: Array<{
    name: string;
    key?: string;
    children: Array<{
      name: string;
      price: number;
      imageUrl?: string;
    }>;
    quantity: number;
  }>;
  timestamp: number;
}

type NormalizedIncludedItem = {
  name: string;
  quantity: number;
  note?: string;
};

type NormalizedComboItem = {
  item_name?: string;
  itemId?: string;
  item_nameNormalized?: string;
  quantity?: number;
  note?: string;
};

type NormalizedVariantChild = {
  name: string;
  price: number;
  imageUrl?: string;
  comboData?: {
    items?: NormalizedComboItem[];
    combo_price?: number;
  };
  includedItems?: NormalizedIncludedItem[];
};

type NormalizedVariantInput = {
  optionName: string;
  variantName: string;
  variantPrice: number;
  variantType?: string;
  actionType?: string;
  isVariant: boolean;
  isMultiple: boolean;
  quantity: number;
  optionId?: string;
  children: NormalizedVariantChild[];
};

const SESSION_STORAGE_KEY = "restaurant-item-cards";
const ORDER_NOTE_STORAGE_KEY = "restaurant-order-note";

export class SessionStorageManager {
  private static normalizeVariantInput(
    variants: any[] | undefined
  ): NormalizedVariantInput[] {
    if (!Array.isArray(variants)) {
      return [];
    }

    const result: NormalizedVariantInput[] = [];

    variants.forEach((entry) => {
      if (!entry || typeof entry !== "object") {
        return;
      }

      const record = entry as Record<string, unknown>;
      const optionNameCandidate =
        (typeof record.optionName === "string" && record.optionName) ||
        (typeof record.option === "string" && record.option) ||
        (typeof record.name === "string" && record.name) ||
        undefined;
      const variantNameCandidate =
        (typeof record.variantName === "string" && record.variantName) ||
        (typeof record.value === "string" && record.value) ||
        (typeof record.selection === "string" && record.selection) ||
        (typeof record.label === "string" && record.label) ||
        (typeof record.choice === "string" && record.choice) ||
        undefined;

      if (!optionNameCandidate || !variantNameCandidate) {
        return;
      }

      const rawPrice =
        record.variantPrice ??
        record.price ??
        record.addonPrice ??
        record.addon_price ??
        0;
      const numericPrice = Number(rawPrice);
      const resolvedPrice = Number.isFinite(numericPrice)
        ? Number(numericPrice)
        : 0;

      const variantType =
        typeof record.variantType === "string"
          ? record.variantType
          : typeof record.action_type === "string"
          ? record.action_type
          : undefined;
      const actionType =
        typeof record.actionType === "string"
          ? record.actionType
          : typeof record.action_type === "string"
          ? record.action_type
          : undefined;
      const isVariant =
        typeof record.isVariant === "boolean"
          ? record.isVariant
          : typeof record.variant === "boolean"
          ? record.variant
          : false;
      const isMultiple =
        typeof record.isMultiple === "boolean"
          ? record.isMultiple
          : typeof record.is_multiple === "boolean"
          ? record.is_multiple
          : false;
      const quantityValue = Number((record as { quantity?: unknown }).quantity);
      const resolvedQuantity = Number.isFinite(quantityValue)
        ? Math.max(1, Math.floor(quantityValue))
        : 1;
      const optionId =
        typeof record.optionId === "string" && record.optionId.trim()
          ? record.optionId.trim()
          : undefined;

      const children = SessionStorageManager.normalizeVariantChildren(
        record,
        variantNameCandidate,
        resolvedPrice
      );

      result.push({
        optionName: optionNameCandidate.trim(),
        variantName: variantNameCandidate.trim(),
        variantPrice: resolvedPrice,
        variantType,
        actionType,
        isVariant,
        isMultiple,
        quantity: resolvedQuantity,
        optionId,
        children,
      });
    });

    return result;
  }

  private static normalizeVariantChildren(
    record: Record<string, unknown>,
    fallbackName: string,
    fallbackPrice: number
  ): NormalizedVariantChild[] {
    const rawChildren = Array.isArray((record as { children?: unknown }).children)
      ? ((record as { children?: unknown }).children as unknown[])
      : [];

    const normalizedChildren = rawChildren
      .map((child) =>
        SessionStorageManager.normalizeVariantChild(
          child,
          fallbackName,
          fallbackPrice
        )
      )
      .filter((child): child is NormalizedVariantChild => child !== null);

    if (normalizedChildren.length > 0) {
      return normalizedChildren;
    }

    return [
      {
        name: fallbackName.trim(),
        price: fallbackPrice,
      },
    ];
  }

  private static normalizeVariantChild(
    child: unknown,
    fallbackName: string,
    fallbackPrice: number
  ): NormalizedVariantChild | null {
    if (!child || typeof child !== "object") {
      return null;
    }

    const childRecord = child as Record<string, unknown>;
    const name =
      typeof childRecord.name === "string" && childRecord.name.trim()
        ? childRecord.name.trim()
        : fallbackName.trim();
    const rawPrice = childRecord.price ?? fallbackPrice;
    const numericPrice = Number(rawPrice);
    const resolvedPrice = Number.isFinite(numericPrice)
      ? Number(numericPrice)
      : fallbackPrice;
    const imageUrl =
      typeof childRecord.imageUrl === "string" && childRecord.imageUrl.trim()
        ? childRecord.imageUrl.trim()
        : undefined;

    const comboData = SessionStorageManager.normalizeComboData(
      childRecord.comboData
    );
    const includedItems = SessionStorageManager.normalizeIncludedItems(
      childRecord.includedItems,
      comboData?.items
    );

    return {
      name,
      price: resolvedPrice,
      imageUrl,
      comboData,
      includedItems,
    };
  }

  private static normalizeIncludedItems(
    rawIncludedItems: unknown,
    comboItems?: NormalizedComboItem[]
  ): NormalizedIncludedItem[] | undefined {
    if (Array.isArray(rawIncludedItems)) {
      const normalized: NormalizedIncludedItem[] = [];
      rawIncludedItems.forEach((item) => {
        const entry = SessionStorageManager.normalizeIncludedItem(item);
        if (entry) {
          normalized.push(entry);
        }
      });
      if (normalized.length > 0) {
        return normalized;
      }
    }

    if (comboItems && Array.isArray(comboItems)) {
      const normalized: NormalizedIncludedItem[] = [];
      comboItems.forEach((item) => {
        if (!item) {
          return;
        }
        const itemName =
          (typeof item.item_name === "string" && item.item_name.trim()) ||
          (typeof item.item_nameNormalized === "string" &&
            item.item_nameNormalized.trim()) ||
          (typeof item.itemId === "string" && item.itemId.trim()) ||
          undefined;
        if (!itemName) {
          return;
        }
        const quantityValue = Number((item as { quantity?: unknown }).quantity);
        const quantity = Number.isFinite(quantityValue)
          ? Math.max(1, Math.floor(quantityValue))
          : 1;
        const note =
          typeof item.note === "string" && item.note.trim()
            ? item.note.trim()
            : undefined;
        normalized.push({ name: itemName, quantity, note });
      });

      if (normalized.length > 0) {
        return normalized;
      }
    }

    return undefined;
  }

  private static normalizeIncludedItem(
    item: unknown
  ): NormalizedIncludedItem | null {
    if (!item || typeof item !== "object") {
      return null;
    }

    const record = item as Record<string, unknown>;
    const name =
      typeof record.name === "string" && record.name.trim()
        ? record.name.trim()
        : undefined;
    if (!name) {
      return null;
    }

    const quantityValue = Number(record.quantity);
    const quantity = Number.isFinite(quantityValue)
      ? Math.max(1, Math.floor(quantityValue))
      : 1;
    const note =
      typeof record.note === "string" && record.note.trim()
        ? record.note.trim()
        : undefined;

    return {
      name,
      quantity,
      note,
    };
  }

  private static normalizeComboData(
    comboData: unknown
  ): NormalizedVariantChild["comboData"] {
    if (!comboData || typeof comboData !== "object") {
      return undefined;
    }

    const record = comboData as Record<string, unknown>;
    const itemsRaw = Array.isArray(record.items) ? record.items : undefined;
    const items = itemsRaw
      ?.map((item) => SessionStorageManager.normalizeComboItem(item))
      .filter((item): item is NormalizedComboItem => item !== null);
    const comboPriceRaw = record.combo_price ?? record.price;
    const numericPrice = Number(comboPriceRaw);
    const combo_price = Number.isFinite(numericPrice)
      ? Number(numericPrice)
      : undefined;

    if (!items && typeof combo_price === "undefined") {
      return undefined;
    }

    return {
      items,
      combo_price,
    };
  }

  private static normalizeComboItem(item: unknown): NormalizedComboItem | null {
    if (!item || typeof item !== "object") {
      return null;
    }

    const record = item as Record<string, unknown>;
    const result: {
      item_name?: string;
      itemId?: string;
      item_nameNormalized?: string;
      quantity?: number;
      note?: string;
    } = {};

    if (typeof record.item_name === "string" && record.item_name.trim()) {
      result.item_name = record.item_name.trim();
    } else if (typeof record.name === "string" && record.name.trim()) {
      result.item_name = record.name.trim();
    }

    if (typeof record.item_nameNormalized === "string" && record.item_nameNormalized.trim()) {
      result.item_nameNormalized = record.item_nameNormalized.trim();
    }

    if (typeof record.itemId === "string" && record.itemId.trim()) {
      result.itemId = record.itemId.trim();
    } else if (typeof record.item_id === "string" && record.item_id.trim()) {
      result.itemId = record.item_id.trim();
    }

    const quantityValue = Number(record.quantity);
    if (Number.isFinite(quantityValue)) {
      result.quantity = Math.max(1, Math.floor(quantityValue));
    }

    if (typeof record.note === "string" && record.note.trim()) {
      result.note = record.note.trim();
    }

    if (
      typeof result.item_name === "undefined" &&
      typeof result.item_nameNormalized === "undefined" &&
      typeof result.itemId === "undefined" &&
      typeof result.quantity === "undefined" &&
      typeof result.note === "undefined"
    ) {
      return null;
    }

    return result;
  }

  private static buildVariantKey(
    variants: Array<{ optionName: string; variantName: string }>
  ): string {
    if (variants.length === 0) {
      return "";
    }

    return JSON.stringify(
      variants
        .map((variant) => ({
          optionName: variant.optionName.trim().toLowerCase(),
          variantName: variant.variantName.trim().toLowerCase(),
        }))
        .sort((a, b) => {
          const optionCompare = a.optionName.localeCompare(b.optionName);
          if (optionCompare !== 0) {
            return optionCompare;
          }
          return a.variantName.localeCompare(b.variantName);
        })
    );
  }

  private static buildVariantGroups(
    variants: NormalizedVariantInput[],
    quantity: number
  ) {
    const groups = new Map<
      string,
      {
        name: string;
        key: string;
        variantType?: string;
        actionType?: string;
        isVariant: boolean;
        isMultiple: boolean;
        quantity: number;
        children: NormalizedVariantChild[];
      }
    >();

    variants.forEach((variant) => {
      const key = variant.optionName.trim().toLowerCase();
      const existing = groups.get(key);
      const normalizedChildren =
        variant.children && variant.children.length > 0
          ? variant.children.map((child) => ({
              ...child,
              price: Number.isFinite(child.price) ? Number(child.price) : 0,
            }))
          : [
              {
                name: variant.variantName,
                price: Number.isFinite(variant.variantPrice)
                  ? Number(variant.variantPrice)
                  : 0,
              },
            ];

      if (existing) {
        normalizedChildren.forEach((child) => {
          const duplicate = existing.children.some(
            (existingChild) =>
              existingChild.name === child.name &&
              existingChild.price === child.price
          );
          if (!duplicate) {
            existing.children.push(child);
          }
        });

        existing.variantType = existing.variantType ?? variant.variantType;
        existing.actionType = existing.actionType ?? variant.actionType;
        existing.isVariant = existing.isVariant || variant.isVariant;
        existing.isMultiple = existing.isMultiple || variant.isMultiple;
        const resolvedQuantity = Number.isFinite(variant.quantity)
          ? Number(variant.quantity)
          : quantity;
        if (resolvedQuantity > existing.quantity) {
          existing.quantity = resolvedQuantity;
        }
        return;
      }

      const resolvedQuantity = Number.isFinite(variant.quantity)
        ? Math.max(1, Math.floor(variant.quantity))
        : Math.max(1, Math.floor(quantity));

      groups.set(key, {
        name: variant.optionName,
        key: variant.optionId ?? variant.optionName,
        variantType: variant.variantType,
        actionType: variant.actionType,
        isVariant: variant.isVariant,
        isMultiple: variant.isMultiple,
        quantity: resolvedQuantity,
        children: normalizedChildren,
      });
    });

    return Array.from(groups.values()).map((group) => ({
      name: group.name,
      key: group.key,
      variantType: group.variantType,
      actionType: group.actionType,
      isVariant: group.isVariant,
      isMultiple: group.isMultiple,
      quantity: Math.max(1, Math.floor(group.quantity || quantity || 1)),
      children: group.children,
    }));
  }

  private static extractSimpleVariantsFromCard(
    variants: Array<{
      name: string;
      key?: string;
      children: Array<{ name: string; price: number; comboData?: any }>;
    }> | undefined
  ) {
    if (!variants) {
      return [] as Array<{ optionName: string; variantName: string }>;
    }

    const result: Array<{ optionName: string; variantName: string }> = [];

    variants.forEach((variant) => {
      if (!variant || typeof variant !== "object") {
        return;
      }

      const optionIdentifier =
        typeof variant.key === "string" && variant.key.trim()
          ? variant.key.trim()
          : typeof variant.name === "string"
          ? variant.name.trim()
          : "";
      if (!optionIdentifier) {
        return;
      }

      variant.children.forEach((child) => {
        if (!child || typeof child !== "object") {
          return;
        }
        if (typeof child.name !== "string") {
          return;
        }

        // Build a comprehensive variant name including combo items if present
        let variantName = child.name;
        
        // If child has comboData with items, include them in the variant identifier
        if (child.comboData?.items && Array.isArray(child.comboData.items)) {
          const comboItemsStr = child.comboData.items
            .map((item: any) => {
              const itemName = item.item_name || item.name || '';
              const qty = item.quantity || 1;
              const note = item.note || '';
              return `${qty}x${itemName}${note ? `(${note})` : ''}`;
            })
            .sort()
            .join('|');
          variantName = `${child.name}[${comboItemsStr}]`;
        }

        result.push({
          optionName: optionIdentifier,
          variantName: variantName,
        });
      });
    });

    return result;
  }

  private static deriveCardPricing(
    variants:
      | Array<{
          actionType?: string;
          isVariant?: boolean;
          children?: Array<{
            price: number;
            comboData?: { combo_price?: number };
          }>;
        }>
      | undefined,
    defaultBasePrice: number
  ) {
    const fallbackBase = Number.isFinite(defaultBasePrice)
      ? Number(defaultBasePrice)
      : 0;
    let unitBasePrice = fallbackBase;
    let unitAddOnTotal = 0;

    if (!Array.isArray(variants) || variants.length === 0) {
      return { unitBasePrice, unitAddOnTotal };
    }

    variants.forEach((variant) => {
      if (!variant || typeof variant !== "object") {
        return;
      }

      const actionType =
        typeof variant.actionType === "string"
          ? variant.actionType.toLowerCase()
          : "";
      const isBaseVariant = variant.isVariant === true;
      const children = Array.isArray(variant.children)
        ? variant.children
        : [];

      children.forEach((child) => {
        if (!child || typeof child !== "object") {
          return;
        }

        const rawPrice = Number((child as { price?: unknown }).price);
        const normalizedPrice = Number.isFinite(rawPrice) ? rawPrice : 0;
        const comboPriceRaw = child.comboData?.combo_price;
        const comboPrice = Number.isFinite(comboPriceRaw)
          ? Number(comboPriceRaw)
          : undefined;

        const shouldOverrideBase =
          comboPrice !== undefined || actionType === "upgrade" || isBaseVariant;

        if (shouldOverrideBase) {
          const overridePrice =
            comboPrice !== undefined ? comboPrice : normalizedPrice;
          unitBasePrice = Number.isFinite(overridePrice)
            ? Number(overridePrice)
            : unitBasePrice;
        } else {
          unitAddOnTotal += normalizedPrice;
        }
      });
    });

    return {
      unitBasePrice,
      unitAddOnTotal,
    };
  }

  static calculateCardPricingSummary(
    variants:
      | Array<{
          actionType?: string;
          isVariant?: boolean;
          children?: Array<{
            price: number;
            comboData?: { combo_price?: number };
          }>;
        }>
      | undefined,
    defaultBasePrice: number
  ) {
    return this.deriveCardPricing(variants, defaultBasePrice);
  }

  // Get all items from session storage
  static getAllItems(): Record<string, SessionStorageItemData> {
    try {
      const existingData = sessionStorage.getItem(SESSION_STORAGE_KEY);
      const items = existingData ? JSON.parse(existingData) : {};
      
      // Ensure all cards have isAiSuggestion field (default to false for backward compatibility)
      Object.keys(items).forEach((key) => {
        if (items[key].cards && Array.isArray(items[key].cards)) {
          items[key].cards = items[key].cards.map((card: any) => ({
            ...card,
            isAiSuggestion: card.isAiSuggestion ?? false
          }));
        }
      });
      
      return items;
    } catch (error) {
      console.error("Error reading session storage:", error);
      return {};
    }
  }

  // Get specific item from session storage
  static getItem(itemId: string): SessionStorageItemData | null {
    const allItems = this.getAllItems();
    return allItems[itemId] || null;
  }

  // Save item to session storage
  static saveItem(itemData: SessionStorageItemData): void {
    try {
      const currentData = this.getAllItems();
      
      // Ensure all cards have isAiSuggestion field (default to false)
      if (itemData.cards && Array.isArray(itemData.cards)) {
        itemData.cards = itemData.cards.map(card => ({
          ...card,
          isAiSuggestion: card.isAiSuggestion ?? false
        }));
      }
      
      currentData[itemData.itemId] = itemData;

      if (Object.keys(currentData).length > 0) {
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(currentData));
      } else {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }

      this.triggerStorageEvent(currentData);
    } catch (error) {
      console.error("Error saving item to session storage:", error);
    }
  }

  // Remove item from session storage
  static removeItem(itemId: string): void {
    try {
      const currentData = this.getAllItems();
      delete currentData[itemId];

      if (Object.keys(currentData).length > 0) {
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(currentData));
      } else {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }
      this.triggerStorageEvent(currentData);
    } catch (error) {
      console.error("Error removing item from session storage:", error);
    }
  }

  static addOrUpdateItemWithVariants(
    item: MenuItem,
    language: string,
    variants: any[] = [],
    quantity: number = 1,
    itemNote?: string,
    isAiSuggestion?: boolean
  ): void {
    if (quantity <= 0) {
      return;
    }

    const normalizedVariants = this.normalizeVariantInput(variants);
    if (normalizedVariants.length === 0) {
      this.addOrIncrementItem(item, language, quantity, itemNote, isAiSuggestion);
      return;
    }

    const variantKey = this.buildVariantKey(normalizedVariants);
    const currentData = this.getAllItems();
    const existingItem = currentData[item.id];

    const itemName = getItemName(item, language);
    const itemDescription = getItemDescription(item, language) || undefined;
    const basePrice = item.discounted_price ?? item.price;
    const itemImage = item.image_url || "";
    const noteProvided = itemNote !== undefined;
    const trimmedNote =
      typeof itemNote === "string" ? itemNote.trim() : "";
    // Get existing notes array or create new one
    const existingNotes = existingItem?.itemNotes ?? [];
    // If a new note is provided, append it to the array
    const normalizedItemNotes: string[] | undefined = noteProvided && trimmedNote.length > 0
      ? [...existingNotes, trimmedNote]
      : existingNotes.length > 0 ? existingNotes : undefined;

    const variantGroups = this.buildVariantGroups(
      normalizedVariants,
      quantity
    ).map((group) => ({
      ...group,
      children: group.children.map((child) => ({ ...child })),
    }));

    console.log('[SessionStorageManager] addOrUpdateItemWithVariants called:', {
      itemId: item.id,
      quantity,
      isAiSuggestion,
      hasExisting: !!existingItem
    });

    if (!existingItem) {
      const initialNotes: string[] | undefined = trimmedNote.length > 0 ? [trimmedNote] : undefined;
      const newItem: SessionStorageItemData = {
        itemId: item.id,
        itemName,
        itemDescription,
        itemImage,
        basePrice,
        totalQuantity: quantity,
        itemNotes: initialNotes,
        cards: [
          {
            cardId: variantKey,
            cardName: itemName,
            quantity,
            variants: variantGroups,
            isAiSuggestion: isAiSuggestion ?? false,
          },
        ],
        timestamp: Date.now(),
      };

      this.saveItem(newItem);
      return;
    }

    const cards = Array.isArray(existingItem.cards)
      ? existingItem.cards.map((card) => ({
          ...card,
          variants: Array.isArray(card.variants)
            ? card.variants.map((variant) => ({
                ...variant,
                quantity: Number.isFinite(variant.quantity)
                  ? Number(variant.quantity)
                  : card.quantity ?? 1,
                children: Array.isArray(variant.children)
                  ? variant.children.map((child) => ({
                      ...child,
                      price: Number.isFinite(child.price)
                        ? Number(child.price)
                        : 0,
                    }))
                  : [],
              }))
            : [],
        }))
      : [];

    const normalizedCards = cards.map((card) => {
      const pricing = SessionStorageManager.calculateCardPricingSummary(
        card.variants,
        basePrice
      );
      return {
        ...card,
        unitBasePrice: pricing.unitBasePrice,
        unitAddOnTotal: pricing.unitAddOnTotal,
      };
    });

    const existingIndex = normalizedCards.findIndex((card) => {
      const simpleVariants = this.extractSimpleVariantsFromCard(card.variants);
      const key = this.buildVariantKey(simpleVariants);
      return key === variantKey;
    });

    if (existingIndex >= 0) {
      const existingCard = normalizedCards[existingIndex];
      const newQuantity = (existingCard.quantity ?? 0) + quantity;
      existingCard.quantity = newQuantity;
      existingCard.variants = existingCard.variants.map((variant) => ({
        ...variant,
        quantity: newQuantity,
      }));
      // Update isAiSuggestion if explicitly provided
      if (isAiSuggestion !== undefined) {
        existingCard.isAiSuggestion = isAiSuggestion;
      }
      // Notes are now managed at item level (itemNotes array), not per-card
    } else {
      const fallbackIndex = normalizedCards.findIndex((card) => {
        const simpleVariants = this.extractSimpleVariantsFromCard(card.variants);
        const key = this.buildVariantKey(simpleVariants);
        return key === "";
      });

      if (fallbackIndex >= 0) {
        const fallbackCard = normalizedCards[fallbackIndex];
        const pricing = SessionStorageManager.calculateCardPricingSummary(
          variantGroups,
          basePrice
        );
        normalizedCards[fallbackIndex] = {
          ...fallbackCard,
          quantity,
          unitBasePrice: pricing.unitBasePrice,
          unitAddOnTotal: pricing.unitAddOnTotal,
          isAiSuggestion: isAiSuggestion ?? false,
          variants: variantGroups.map((group) => ({
            ...group,
            children: group.children.map((child) => ({ ...child })),
          })),
        };
      } else {
        const nextCardNumber = normalizedCards.length + 1;
        const pricing = SessionStorageManager.calculateCardPricingSummary(
          variantGroups,
          basePrice
        );
        normalizedCards.push({
          cardId: `${item.id}-${Date.now()}-${nextCardNumber}`,
          cardName: `${itemName} #${nextCardNumber}`,
          quantity,
          unitBasePrice: pricing.unitBasePrice,
          unitAddOnTotal: pricing.unitAddOnTotal,
          isAiSuggestion: isAiSuggestion ?? false,
          variants: variantGroups,
        });
      }
    }

    const nextTotalQuantity = normalizedCards.reduce(
      (sum, card) => sum + (card.quantity ?? 0),
      0
    );

    const updatedItem: SessionStorageItemData = {
      ...existingItem,
      itemName,
      itemDescription,
      itemImage,
      basePrice,
      totalQuantity: nextTotalQuantity,
      cards: normalizedCards,
      timestamp: Date.now(),
      itemNotes: normalizedItemNotes,
    };

    if (!normalizedItemNotes || normalizedItemNotes.length === 0) {
      delete updatedItem.itemNotes;
    }

    this.saveItem(updatedItem);
  }

  static removeItemVariants(
    itemId: string,
    variants: any[] = [],
    quantity: number = 1
  ): void {
    if (quantity < 0) {
      return;
    }

    const normalizedVariants = this.normalizeVariantInput(variants);
    if (normalizedVariants.length === 0) {
      this.removeItem(itemId);
      return;
    }

    const simpleTargetVariants = normalizedVariants
      .map((variant) => ({
        optionName: (variant.optionName ?? "").toString().trim().toLowerCase(),
        variantName: (variant.variantName ?? "").toString().trim().toLowerCase(),
      }))
      .filter((variant) => variant.optionName && variant.variantName);

    const currentData = this.getAllItems();
    const existingItem = currentData[itemId];
    if (!existingItem || !Array.isArray(existingItem.cards)) {
      return;
    }

    const variantKey = this.buildVariantKey(normalizedVariants);
    const cards = existingItem.cards.map((card) => ({
      ...card,
      variants: Array.isArray(card.variants)
        ? card.variants.map((variant) => ({
            ...variant,
            children: Array.isArray(variant.children)
              ? variant.children.map((child) => ({
                  ...child,
                  price: Number.isFinite(child.price)
                    ? Number(child.price)
                    : 0,
                }))
              : [],
          }))
        : [],
    }));

    const normalizedCards = cards.map((card) => {
      const pricing = SessionStorageManager.calculateCardPricingSummary(
        card.variants,
        existingItem.basePrice
      );
      return {
        ...card,
        unitBasePrice: pricing.unitBasePrice,
        unitAddOnTotal: pricing.unitAddOnTotal,
      };
    });

    let exactMatchIndex = -1;
    let subsetMatchIndex = -1;

    normalizedCards.forEach((card, index) => {
      const simpleVariants = this.extractSimpleVariantsFromCard(card.variants)
        .map((variant) => ({
          optionName: (variant.optionName ?? "").toString().trim().toLowerCase(),
          variantName: (variant.variantName ?? "").toString().trim().toLowerCase(),
        }))
        .filter((variant) => variant.optionName && variant.variantName);
      const key = this.buildVariantKey(simpleVariants);

      if (key === variantKey) {
        exactMatchIndex = index;
        return;
      }

      if (
        simpleTargetVariants.length > 0 &&
        simpleTargetVariants.every((targetVariant) =>
          simpleVariants.some(
            (cardVariant) =>
              cardVariant.optionName === targetVariant.optionName &&
              cardVariant.variantName === targetVariant.variantName
          )
        )
      ) {
        subsetMatchIndex = index;
      }
    });

    if (subsetMatchIndex >= 0 && exactMatchIndex === -1 && quantity === 0) {
      const targetCard = normalizedCards[subsetMatchIndex];
      const updatedVariants = (targetCard.variants || [])
        .map((variantGroup) => {
          if (!variantGroup || typeof variantGroup !== "object") {
            return variantGroup;
          }

          const groupName =
            typeof (variantGroup as any).name === "string"
              ? ((variantGroup as any).name as string).trim().toLowerCase()
              : "";

          if (!groupName) {
            return variantGroup;
          }

          const matchingTargets = simpleTargetVariants.filter(
            (variant) => variant.optionName === groupName
          );

          if (matchingTargets.length === 0) {
            return variantGroup;
          }

          const childrenArray = Array.isArray((variantGroup as any).children)
            ? ((variantGroup as any).children as any[])
            : [];

          const filteredChildren = childrenArray.filter((child) => {
            if (!child || typeof child.name !== "string") {
              return true;
            }
            const childName = child.name.trim().toLowerCase();
            return !matchingTargets.some(
              (target) => target.variantName === childName
            );
          });

          if (filteredChildren.length === 0) {
            return null;
          }

          return {
            ...variantGroup,
            children: filteredChildren,
          };
        })
        .filter((group) => group !== null) as typeof targetCard.variants;

      const hasVariantsLeft =
        updatedVariants.length > 0 &&
        updatedVariants.some((group) =>
          Array.isArray(group.children) && group.children.length > 0
        );

      const recalculated = SessionStorageManager.calculateCardPricingSummary(
        hasVariantsLeft ? updatedVariants : [],
        existingItem.basePrice
      );

      normalizedCards[subsetMatchIndex] = {
        ...targetCard,
        variants: hasVariantsLeft ? updatedVariants : [],
        unitBasePrice: recalculated.unitBasePrice,
        unitAddOnTotal: recalculated.unitAddOnTotal,
      };

      const updatedTotalQuantity = normalizedCards.reduce(
        (sum, card) => sum + (card.quantity || 0),
        0
      );

      const updatedItem: SessionStorageItemData = {
        ...existingItem,
        cards: normalizedCards,
        totalQuantity: updatedTotalQuantity,
        timestamp: Date.now(),
      };

      this.saveItem(updatedItem);
      return;
    }

    if (exactMatchIndex === -1) {
      return;
    }

    const targetCard = normalizedCards[exactMatchIndex];

    // Handle variant-only removal (quantity = 0) for exact matches
    if (quantity === 0) {
      // Convert to base item by removing all variants
      const recalculated = SessionStorageManager.calculateCardPricingSummary(
        [],
        existingItem.basePrice
      );

      normalizedCards[exactMatchIndex] = {
        ...targetCard,
        variants: [],
        unitBasePrice: recalculated.unitBasePrice,
        unitAddOnTotal: recalculated.unitAddOnTotal,
      };

      const updatedTotalQuantity = normalizedCards.reduce(
        (sum, card) => sum + (card.quantity || 0),
        0
      );

      const updatedItem: SessionStorageItemData = {
        ...existingItem,
        cards: normalizedCards,
        totalQuantity: updatedTotalQuantity,
        timestamp: Date.now(),
      };

      this.saveItem(updatedItem);
      return;
    }

    const newQuantity = Math.max(0, (targetCard.quantity || 0) - quantity);

    if (newQuantity <= 0) {
      normalizedCards.splice(exactMatchIndex, 1);
    } else {
      targetCard.quantity = newQuantity;
      targetCard.variants = targetCard.variants.map((variant) => ({
        ...variant,
        quantity: newQuantity,
      }));
    }

    const remainingQuantity = normalizedCards.reduce(
      (sum, card) => sum + (card.quantity || 0),
      0
    );

    if (remainingQuantity <= 0) {
      this.removeItem(itemId);
      return;
    }

    const updatedItem: SessionStorageItemData = {
      ...existingItem,
      cards: normalizedCards,
      totalQuantity: remainingQuantity,
      timestamp: Date.now(),
    };

    this.saveItem(updatedItem);
  }

  static addOrIncrementItem(
    item: MenuItem,
    language: string,
    quantity: number = 1,
    itemNote?: string,
    isAiSuggestion?: boolean
  ): void {
    if (quantity <= 0) {
      return;
    }

    const noteProvided = itemNote !== undefined;
    const trimmedNote =
      typeof itemNote === "string" ? itemNote.trim() : "";
    const normalizedItemNote = noteProvided
      ? trimmedNote.length > 0
        ? trimmedNote
        : undefined
      : undefined;
    const aiFlag = isAiSuggestion ?? false;

    const existing = this.getItem(item.id);
    if (existing) {
      const currentData = this.getAllItems();
      
      if (currentData[item.id]) {
        if (!currentData[item.id].cards) {
          currentData[item.id].cards = [];
        }
        
        // Find existing card with no variants and matching isAiSuggestion flag to merge with
        const existingCardIndex = currentData[item.id].cards!.findIndex(
          (card) => 
            (!card.variants || card.variants.length === 0) &&
            card.isAiSuggestion === aiFlag
        );
        
        if (existingCardIndex >= 0) {
          // Merge with existing card - increment quantity
          currentData[item.id].cards![existingCardIndex].quantity += quantity;
        } else {
          // Create a single new card with the full quantity
          const nextCardNumber = currentData[item.id].cards!.length + 1;
          const newCard = {
            cardId: `${item.id}-${Date.now()}-${nextCardNumber}`,
            cardName: `${getItemName(item, language)} #${nextCardNumber}`,
            quantity: quantity,
            unitBasePrice: item.discounted_price ?? item.price,
            unitAddOnTotal: 0,
            isAiSuggestion: aiFlag,
            variants: [],
          };
          currentData[item.id].cards!.push(newCard);
        }
        
        // Recalculate total quantity from all cards
        currentData[item.id].totalQuantity = currentData[item.id].cards!.reduce(
          (sum, card) => sum + (card.quantity || 0),
          0
        );
        
        // Update notes if provided - append to array
        if (noteProvided && trimmedNote) {
          const existingNotes = currentData[item.id].itemNotes ?? [];
          currentData[item.id].itemNotes = [...existingNotes, trimmedNote];
        }
        
        // Save and trigger event
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(currentData));
        this.triggerStorageEvent(currentData);
        return;
      }
      
      // Fallback to updateItemQuantity if direct update fails
      this.updateItemQuantity(item.id, existing.totalQuantity + quantity, isAiSuggestion);
      if (noteProvided && trimmedNote) {
        this.addItemNote(item.id, trimmedNote);
      }
      return;
    }

    // Create new item with a single card containing the full quantity
    const itemName = getItemName(item, language);
    const itemDescription = getItemDescription(item, language) || undefined;
    const basePrice = item.discounted_price ?? item.price;
    const itemImage = item.image_url || '';
    
    const newItem: SessionStorageItemData = {
      itemId: item.id,
      itemName,
      itemDescription,
      itemImage,
      basePrice,
      totalQuantity: quantity,
      itemNotes: normalizedItemNote ? [normalizedItemNote] : undefined,
      cards: [{
        cardId: `${item.id}-${Date.now()}-1`,
        cardName: `${itemName} #1`,
        quantity: quantity,
        unitBasePrice: basePrice,
        unitAddOnTotal: 0,
        isAiSuggestion: aiFlag,
        variants: [],
      }],
      timestamp: Date.now(),
    };

    this.saveItem(newItem);
  }

  // Update item cards in session storage
  static updateItemCards(
    item: MenuItem,
    itemCards: ItemCard[],
    language: string,
    hasUserInteracted: boolean
  ): void {
    try {
      const currentData = this.getAllItems();

      if (itemCards.length === 0) {
        if (hasUserInteracted) {
          // User deliberately removed items
          delete currentData[item.id];
        } else {
          // Component loading, preserve existing data
          return;
        }
      } else {
        // Build cards array with variants
        const cardsArray = itemCards.map((card) => {
          const itemOptions = (item as unknown as { options?: any[] }).options;
          const cardVariants = itemOptions
            ? itemOptions
                .map(
                  (option: any): {
                    name: string;
                    key: string;
                    children: Array<{ name: string; price: number; imageUrl?: string }>;
                    quantity: number;
                  } | null => {
                  const optionKey = getOptionStorageKey(option);
                  const legacyKey = option.name;
                  const selected =
                    card.variants[optionKey] ?? card.variants[legacyKey];
                  if (selected) {
                    if (
                      option.isMultipleSelection &&
                      Array.isArray(selected) &&
                      selected.length > 0
                    ) {
                      const selectedChoices =
                        option.children?.filter((choice: any) =>
                          selected.includes(choice.name)
                        ) || [];

                      return {
                        name: option.name,
                        key: optionKey,
                        children: selectedChoices.map((choice: any) => ({
                          name: choice.name,
                          price: choice.price || 0,
                          imageUrl: choice.imageUrl || "",
                        })),
                        quantity: card.quantity,
                      };
                    } else if (!option.isMultipleSelection && selected) {
                      const selectedChoice = option.children?.find(
                        (choice: any) => choice.name === selected
                      );

                      return {
                        name: option.name,
                        key: optionKey,
                        children: [
                          {
                            name: selected,
                            price: selectedChoice?.price || 0,
                            imageUrl: selectedChoice?.imageUrl || "",
                          },
                        ],
                        quantity: card.quantity,
                      };
                    }
                  }
                  return null;
                }
                )
                .filter(
                  (
                    variant
                  ): variant is {
                    name: string;
                    key: string;
                    children: Array<{ name: string; price: number; imageUrl?: string }>;
                    quantity: number;
                  } => variant !== null
                )
            : [];

          // Preserve isAiSuggestion from existing card if available
          const existingCard = existingItem?.cards?.find((c: any) => c.cardId === card.id);
          
          return {
            cardId: card.id,
            cardName: card.name,
            quantity: card.quantity,
            ...SessionStorageManager.calculateCardPricingSummary(
              cardVariants,
              item.discounted_price || item.price
            ),
            isAiSuggestion: existingCard?.isAiSuggestion ?? false,
            variants:
              cardVariants.length > 0
                ? cardVariants
                : [
                    {
                      name: "",
                      key: "",
                      children: [],
                      quantity: card.quantity,
                    },
                  ],
          };
        });

        const totalQuantity = itemCards.reduce((sum, card) => sum + card.quantity, 0);

        const existingItem = currentData[item.id];
        const itemData: SessionStorageItemData = {
          itemId: item.id,
          itemName: getItemName(item, language),
          itemDescription: getItemDescription(item, language) || undefined,
          itemImage: item.image_url || "",
          basePrice: item.discounted_price || item.price,
          totalQuantity,
          cards: cardsArray as any,
          timestamp: Date.now(),
        };

        currentData[item.id] = itemData;
      }

      // Save updated data
      if (Object.keys(currentData).length > 0) {
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(currentData));
      } else {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }

      this.triggerStorageEvent(currentData);
    } catch (error) {
      console.error("Error updating session storage with cards:", error);
    }
  }

  // Update item quantity (for simple quantity changes)
  static updateItemQuantity(itemId: string, newQuantity: number, isAiSuggestion?: boolean): void {
    try {
      const currentData = this.getAllItems();
      
      if (newQuantity <= 0) {
        delete currentData[itemId];
      } else if (currentData[itemId]) {
        const item = currentData[itemId];
        const originalQuantity = item.totalQuantity;
        item.totalQuantity = newQuantity;

        // Handle cards structure
        if (item.cards && item.cards.length > 0) {
          const quantityDiff = newQuantity - originalQuantity;

          if (quantityDiff > 0) {
            // Add cards
            for (let i = 0; i < quantityDiff; i++) {
              const newCard = {
                cardId: `${itemId}-${Date.now()}-${item.cards.length + i + 1}`,
                cardName: `${item.itemName} #${item.cards.length + i + 1}`,
                quantity: 1,
                unitBasePrice: item.basePrice,
                unitAddOnTotal: 0,
                isAiSuggestion: isAiSuggestion ?? false,
                variants: [
                  {
                    name: "",
                    children: [],
                    quantity: 1,
                  },
                ],
              };
              item.cards.push(newCard);
            }
          } else if (quantityDiff < 0) {
            // Remove cards
            const cardsToRemove = Math.abs(quantityDiff);
            item.cards.splice(-cardsToRemove, cardsToRemove);
          }
        }
      }

      // Save updated data
      if (Object.keys(currentData).length > 0) {
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(currentData));
      } else {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }

      this.triggerStorageEvent(currentData);
    } catch (error) {
      console.error("Error updating item quantity:", error);
    }
  }

  // Clear all items from session storage
  static clearAll(): void {
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      sessionStorage.removeItem(ORDER_NOTE_STORAGE_KEY);
      this.triggerStorageEvent({});
      this.triggerOrderNoteStorageEvent("");
    } catch (error) {
      console.error("Error clearing session storage:", error);
    }
  }

  static getOrderNote(): string {
    try {
      const storedNote = sessionStorage.getItem(ORDER_NOTE_STORAGE_KEY);
      return storedNote || "";
    } catch (error) {
      console.error("Error reading order note from session storage:", error);
      return "";
    }
  }

  static saveOrderNote(note: string): void {
    try {
      const trimmedNote = typeof note === "string" ? note.trim() : "";
      if (trimmedNote) {
        sessionStorage.setItem(ORDER_NOTE_STORAGE_KEY, trimmedNote);
      } else {
        sessionStorage.removeItem(ORDER_NOTE_STORAGE_KEY);
      }
      this.triggerOrderNoteStorageEvent(trimmedNote);
    } catch (error) {
      console.error("Error saving order note to session storage:", error);
    }
  }

  static clearOrderNote(): void {
    try {
      sessionStorage.removeItem(ORDER_NOTE_STORAGE_KEY);
      this.triggerOrderNoteStorageEvent("");
    } catch (error) {
      console.error("Error clearing order note from session storage:", error);
    }
  }

  // Legacy method - now just adds a note to the array
  static updateItemNote(itemId: string, note: string): void {
    this.addItemNote(itemId, note);
  }

  // Add a note to the item's notes array
  static addItemNote(itemId: string, note: string): void {
    try {
      const existing = this.getItem(itemId);
      if (!existing) {
        return;
      }

      const trimmedNote = typeof note === "string" ? note.trim() : "";
      if (!trimmedNote) {
        return;
      }

      const existingNotes = existing.itemNotes ?? [];
      const nextItem: SessionStorageItemData = {
        ...existing,
        itemNotes: [...existingNotes, trimmedNote],
        timestamp: Date.now(),
      };

      this.saveItem(nextItem);
    } catch (error) {
      console.error("Error adding item note in session storage:", error);
    }
  }

  // Update a specific note at index in the item's notes array
  static updateItemNoteAtIndex(itemId: string, noteIndex: number, newNote: string): void {
    try {
      const existing = this.getItem(itemId);
      if (!existing || !existing.itemNotes) {
        return;
      }

      const trimmedNote = typeof newNote === "string" ? newNote.trim() : "";
      const updatedNotes = [...existing.itemNotes];
      
      if (noteIndex < 0 || noteIndex >= updatedNotes.length) {
        return;
      }

      if (trimmedNote) {
        updatedNotes[noteIndex] = trimmedNote;
      } else {
        // Remove if empty
        updatedNotes.splice(noteIndex, 1);
      }

      const nextItem: SessionStorageItemData = {
        ...existing,
        itemNotes: updatedNotes.length > 0 ? updatedNotes : undefined,
        timestamp: Date.now(),
      };

      if (!nextItem.itemNotes || nextItem.itemNotes.length === 0) {
        delete nextItem.itemNotes;
      }

      this.saveItem(nextItem);
    } catch (error) {
      console.error("Error updating item note in session storage:", error);
    }
  }

  // Delete a specific note at index from the item's notes array
  static deleteItemNote(itemId: string, noteIndex: number): void {
    try {
      const existing = this.getItem(itemId);
      if (!existing || !existing.itemNotes) {
        return;
      }

      const updatedNotes = [...existing.itemNotes];
      
      if (noteIndex < 0 || noteIndex >= updatedNotes.length) {
        return;
      }

      updatedNotes.splice(noteIndex, 1);

      const nextItem: SessionStorageItemData = {
        ...existing,
        itemNotes: updatedNotes.length > 0 ? updatedNotes : undefined,
        timestamp: Date.now(),
      };

      if (!nextItem.itemNotes || nextItem.itemNotes.length === 0) {
        delete nextItem.itemNotes;
      }

      this.saveItem(nextItem);
    } catch (error) {
      console.error("Error deleting item note in session storage:", error);
    }
  }

  // Legacy method - kept for backward compatibility but no longer used
  static updateCardNote(cardId: string, note: string): void {
    // Cards no longer have notes - find item and add to itemNotes instead
    try {
      const currentData = this.getAllItems();

      Object.keys(currentData).forEach((itemId) => {
        const item = currentData[itemId];
        if (item.cards && Array.isArray(item.cards)) {
          const hasCard = item.cards.some((c) => c.cardId === cardId);
          if (hasCard) {
            const trimmedNote = typeof note === "string" ? note.trim() : "";
            if (trimmedNote) {
              this.addItemNote(itemId, trimmedNote);
            }
          }
        }
      });
    } catch (error) {
      console.error("Error updating card note in session storage:", error);
    }
  }

  // Sort items by itemId (alphabetically then numerically)
  static getSortedItems(): SessionStorageItemData[] {
    const allItems = this.getAllItems();
    let items = Object.values(allItems);

    return items.sort((a, b) => {
      // Split itemId into alpha and numeric parts
      const regex = /([a-zA-Z]+)(\d*)/;
      const aMatch = a.itemId.match(regex);
      const bMatch = b.itemId.match(regex);
      
      if (aMatch && bMatch) {
        // Compare alpha part
        if (aMatch[1] !== bMatch[1]) {
          return aMatch[1].localeCompare(bMatch[1]);
        }
        // Compare numeric part
        const aNum = aMatch[2] ? parseInt(aMatch[2], 10) : 0;
        const bNum = bMatch[2] ? parseInt(bMatch[2], 10) : 0;
        return aNum - bNum;
      }
      
      // Fallback to string compare
      return a.itemId.localeCompare(b.itemId);
    });
  }

  // Trigger storage event for components listening
  private static triggerStorageEvent(data: Record<string, SessionStorageItemData>): void {
    window.dispatchEvent(
      new StorageEvent("storage", {
        key: SESSION_STORAGE_KEY,
        newValue: Object.keys(data).length > 0 ? JSON.stringify(data) : null,
        storageArea: sessionStorage,
      })
    );
    this.triggerCartStorageUpdate();
  }

  private static triggerOrderNoteStorageEvent(note: string): void {
    window.dispatchEvent(
      new StorageEvent("storage", {
        key: ORDER_NOTE_STORAGE_KEY,
        newValue: note.length > 0 ? note : null,
        storageArea: sessionStorage,
      })
    );
    this.triggerCartStorageUpdate();
  }

  private static triggerCartStorageUpdate(): void {
    window.dispatchEvent(new CustomEvent("cartStorageUpdate"));
  }

  // Get total items count
  static getTotalItemsCount(): number {
    const items = Object.values(this.getAllItems());
    return items.reduce((total, item) => total + item.totalQuantity, 0);
  }

  // Get total cart value
  static getTotalCartValue(): number {
    const items = Object.values(this.getAllItems());
    return items.reduce((total, item) => {
      const defaultBasePrice = Number.isFinite(item.basePrice)
        ? Number(item.basePrice)
        : 0;

      if (item.cards && item.cards.length > 0) {
        const itemTotal = item.cards.reduce((cardTotal, card) => {
          const quantity = Number.isFinite(card.quantity)
            ? Number(card.quantity)
            : 0;
          if (quantity <= 0) {
            return cardTotal;
          }

          const pricing = SessionStorageManager.calculateCardPricingSummary(
            card.variants,
            defaultBasePrice
          );

          const unitBasePrice = Number.isFinite(card.unitBasePrice)
            ? Number(card.unitBasePrice)
            : pricing.unitBasePrice;
          const unitAddOnTotal = Number.isFinite(card.unitAddOnTotal)
            ? Number(card.unitAddOnTotal)
            : pricing.unitAddOnTotal;

          return cardTotal + (unitBasePrice + unitAddOnTotal) * quantity;
        }, 0);

        return total + itemTotal;
      }

      const baseTotal = defaultBasePrice * item.totalQuantity;

      // Calculate variants cost for legacy structure
      let variantsTotal = 0;
      if (item.variants && item.variants.length > 0) {
        variantsTotal = item.variants.reduce((variantTotal, variant) => {
          return (
            variantTotal +
            variant.children.reduce((childTotal, child) => {
              return childTotal + child.price * variant.quantity;
            }, 0)
          );
        }, 0);
      }

      return total + baseTotal + variantsTotal;
    }, 0);
  }
}
